/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

// Adnami skin/takeover format support. One file per vendor format lives under
// formats/vendor/<vendor>/; site-managed (non-vendor) formats get their own
// package alongside vendor/.

// All selectors/attributes are owned by the Adnami engine (adsm.macro.rmb.js),
// not by publishers, so they hold across properties.

export const ADNAMI_VENDOR = 'adnami';

// Iframe ids follow `adsm-iframe-<name-or-cc>-<sid>`; the per-render sid is
// always the segment after the final dash.
export const ADNAMI_IFRAME_ID_PREFIX = 'adsm-iframe-';

export const BANNER_PARENT_CLASS = 'adsm-skin-banner-parent';

export const SID_ATTRIBUTE = 'data-adnm-sid';
export const FID_ATTRIBUTE = 'data-adnm-fid';

// Markers that identify a slot container co-opted by an Adnami skin. The
// `ins.adnm-tag` element is part of the GAM creative markup itself, so it is
// present synchronously at render for direct-sold buys.
export const CONTAINER_MARKER_SELECTOR =
  `ins.adnm-tag, [data-adnm-cc], [${SID_ATTRIBUTE}], ` +
  `iframe[id^="${ADNAMI_IFRAME_ID_PREFIX}"], .adsm-skin-creative`;

// Markers looked for inside the rendered (friendly) ad iframe document.
export const IFRAME_MARKER_SELECTOR = 'ins.adnm-tag, [data-adnm-cc], script[src*="adnami.io"]';

// Opportunistic signature for Prebid bid `ad` markup. Not load-bearing: some
// SSPs return wrapper tags where the Adnami markup only appears after
// resolution.
export const BID_MARKUP_SIGNATURE = /adnami\.io|adnm-tag/i;

// Bounded re-check window for markers that are injected asynchronously after
// the render (the macro creative fetches its directive config before building
// the wallpaper).
export const DEFAULT_DETECT_MS = 2000;

// Re-check points, as fractions of the detect window.
export const RECHECK_STEPS = [0.125, 0.25, 0.5, 1];

import { Logger } from '../../../../logger';
import type { FormatDetection } from '../../../../types/formats';
import type { FormatInteractionCallback, VendorFormatAdapter } from '../../index';

declare global {
  interface Window {
    adsm?: object;
    adsmtag?: object;
  }
}

const logger = Logger.getInstance();

/**
 * Detects Adnami skin/takeover renders and captures interactions on the
 * skin surfaces that live outside the host placement's DOM (wallpaper rails,
 * seamless backgrounds, overlays).
 *
 * One instance is owned by one Placement and is scoped to a single render:
 * the owning placement disposes it on re-render. Vendor presence on the page
 * is a factory concern (VENDOR_FORMAT_REGISTRY probe), not the adapter's.
 */
class AdnamiSkinAdapter implements VendorFormatAdapter {
  public readonly vendor = ADNAMI_VENDOR;

  private container: HTMLElement | null = null;
  private detection: FormatDetection | null = null;

  private detectionTimers: number[] = [];
  private armingTimers: number[] = [];

  private armedSurfaces: HTMLIFrameElement[] = [];
  private surfaceMouseEnterListener?: () => void;
  private surfaceMouseLeaveListener?: () => void;
  private winBlurListener?: () => void;
  private mouseOverSurface = false;

  /**
   * Synchronous detection against the placement's slot container (and its
   * rendered friendly iframe), plus the optional Prebid bid markup. Returns
   * the current best detection, or null when nothing Adnami is found.
   */
  public inspectRender(container: HTMLElement | null, bidAdMarkup?: string): FormatDetection | null {
    this.container = container;

    let detected = false;
    if (container) {
      detected =
        container.classList.contains(BANNER_PARENT_CLASS) ||
        container.querySelector(CONTAINER_MARKER_SELECTOR) !== null;

      if (!detected) {
        // The rendered ad iframe is friendly for skin buys (they must break
        // out), so its document is inspectable. Cross-origin access throws;
        // treat as not-detected.
        const adFrame = container.querySelector('iframe');
        try {
          const frameDoc = adFrame?.contentWindow?.document;
          detected = !!frameDoc && frameDoc.querySelector(IFRAME_MARKER_SELECTOR) !== null;
        } catch (e) {}
      }
    }

    if (!detected && bidAdMarkup && BID_MARKUP_SIGNATURE.test(bidAdMarkup)) {
      detected = true;
    }

    if (!detected) {
      return null;
    }

    const groupId = this.findGroupId(container);
    const formatId = this.findFormatId(container, groupId);
    this.detection = { vendor: this.vendor, formatId, groupId };
    return this.detection;
  }

  /**
   * Bounded re-checks for markers that appear after the render (the macro
   * creative fetches its config before building the wallpaper — which is also
   * the only carrier of the format id). Invokes the callback at most once,
   * with the first complete (or final best) detection.
   */
  public watchForLateDetection(
    container: HTMLElement | null,
    callback: (detection: FormatDetection) => void,
  ): void {
    this.clearTimers(this.detectionTimers);
    const detectWindow = DEFAULT_DETECT_MS;
    let notified = false;

    const notify = (detection: FormatDetection) => {
      if (!notified) {
        notified = true;
        this.clearTimers(this.detectionTimers);
        callback(detection);
      }
    };

    RECHECK_STEPS.forEach((step, index) => {
      const isLastStep = index === RECHECK_STEPS.length - 1;
      const timer = window.setTimeout(() => {
        const detection = this.inspectRender(container);
        if (
          detection &&
          ((detection.groupId !== null && detection.formatId !== null) || isLastStep)
        ) {
          notify(detection);
        }
      }, detectWindow * step);
      this.detectionTimers.push(timer);
    });
  }

  /**
   * Arm interaction capture on every Adnami surface belonging to this render
   * that lives OUTSIDE the host container: fluidskin rails, seamless-skin
   * backgrounds, overlays. Surfaces are matched by the per-render sid suffix
   * (name-agnostic) and are cross-origin sandboxes, so capture uses the
   * window-blur + activeElement heuristic with hover corroboration.
   *
   * Surfaces are injected asynchronously, so scanning retries on the same
   * bounded schedule as detection.
   */
  public armInteractionCapture(onInteraction: FormatInteractionCallback): void {
    this.clearTimers(this.armingTimers);
    const detectWindow = DEFAULT_DETECT_MS;

    const scan = () => {
      if (this.armedSurfaces.length > 0) {
        return;
      }
      const surfaces = this.findExternalSurfaces();
      if (surfaces.length === 0) {
        return;
      }
      this.clearTimers(this.armingTimers);
      this.armSurfaces(surfaces, onInteraction);
    };

    scan();
    if (this.armedSurfaces.length === 0) {
      RECHECK_STEPS.forEach((step) => {
        this.armingTimers.push(window.setTimeout(scan, detectWindow * step));
      });
    }
  }

  public dispose(): void {
    this.clearTimers(this.detectionTimers);
    this.clearTimers(this.armingTimers);
    this.disarmSurfaces();
    this.container = null;
    this.detection = null;
  }

  private armSurfaces(surfaces: HTMLIFrameElement[], onInteraction: FormatInteractionCallback): void {
    this.armedSurfaces = surfaces;
    this.surfaceMouseEnterListener = () => {
      this.mouseOverSurface = true;
    };
    this.surfaceMouseLeaveListener = () => {
      this.mouseOverSurface = false;
    };
    surfaces.forEach((surface) => {
      surface.addEventListener('mouseenter', this.surfaceMouseEnterListener!);
      surface.addEventListener('mouseleave', this.surfaceMouseLeaveListener!);
    });

    this.winBlurListener = () => {
      // Focus moving into one of the skin's cross-origin sandboxes (a click
      // inside them steals focus before opening the clickthrough tab).
      const activeElement = document.activeElement;
      if (activeElement && this.armedSurfaces.some((s) => s === activeElement)) {
        this.fireInteraction(onInteraction, 'SkinInteractionTriggerBlurActiveElement');
        return;
      }
      if (this.mouseOverSurface) {
        this.fireInteraction(onInteraction, 'SkinInteractionTriggerBlurHover');
      }
    };
    window.addEventListener('blur', this.winBlurListener);
    logger.debug(`Adnami skin interaction capture armed on ${surfaces.length} surface(s)`);
  }

  private fireInteraction(onInteraction: FormatInteractionCallback, trigger: string): void {
    // One-shot per render: the owning placement's per-render latch also
    // dedupes, but removing the listener avoids repeat work on later blurs.
    if (this.winBlurListener) {
      window.removeEventListener('blur', this.winBlurListener);
      this.winBlurListener = undefined;
    }
    onInteraction(trigger);
  }

  private disarmSurfaces(): void {
    this.armedSurfaces.forEach((surface) => {
      if (this.surfaceMouseEnterListener) {
        surface.removeEventListener('mouseenter', this.surfaceMouseEnterListener);
      }
      if (this.surfaceMouseLeaveListener) {
        surface.removeEventListener('mouseleave', this.surfaceMouseLeaveListener);
      }
    });
    if (this.winBlurListener) {
      window.removeEventListener('blur', this.winBlurListener);
    }
    this.armedSurfaces = [];
    this.surfaceMouseEnterListener = undefined;
    this.surfaceMouseLeaveListener = undefined;
    this.winBlurListener = undefined;
    this.mouseOverSurface = false;
  }

  private findExternalSurfaces(): HTMLIFrameElement[] {
    const sid = this.detection?.groupId ?? this.findGroupId(this.container);
    const candidates = [
      ...document.querySelectorAll<HTMLIFrameElement>(`iframe[id^="${ADNAMI_IFRAME_ID_PREFIX}"]`),
    ];
    return candidates.filter((frame) => {
      if (this.container && this.container.contains(frame)) {
        return false;
      }
      // Match this render's surfaces by sid suffix. Without a sid (not yet
      // discoverable) fall back to all external Adnami frames: at most one
      // skin renders per page in practice, and arming is harmless.
      return sid ? frame.id.endsWith(`-${sid}`) : true;
    });
  }

  private findGroupId(container: HTMLElement | null): string | null {
    // The host container is authoritative for this render's sid; body-level
    // carriers belong to the wallpaper and could be another skin's.
    const inContainer = container
      ?.querySelector(`[${SID_ATTRIBUTE}]`)
      ?.getAttribute(SID_ATTRIBUTE);
    if (inContainer) {
      return inContainer;
    }
    // Banner iframes (`adsm-iframe-<cc>-<sid>`) carry no sid attribute; the
    // sid is always the segment after the final dash of the iframe id.
    const frame = container?.querySelector(`iframe[id^="${ADNAMI_IFRAME_ID_PREFIX}"]`);
    if (frame) {
      const segments = frame.id.split('-');
      const candidate = segments[segments.length - 1];
      if (candidate) {
        return candidate;
      }
    }
    return document.querySelector(`[${SID_ATTRIBUTE}]`)?.getAttribute(SID_ATTRIBUTE) ?? null;
  }

  private findFormatId(container: HTMLElement | null, groupId: string | null): string | null {
    const inContainer = container?.querySelector(`[${FID_ATTRIBUTE}]`);
    if (inContainer) {
      return inContainer.getAttribute(FID_ATTRIBUTE);
    }
    // The format id usually rides on the body-level wallpaper elements; scope
    // by sid when known so concurrent skins cannot cross-contaminate.
    const selector = groupId
      ? `[${SID_ATTRIBUTE}="${groupId}"][${FID_ATTRIBUTE}]`
      : `[${FID_ATTRIBUTE}]`;
    return document.querySelector(selector)?.getAttribute(FID_ATTRIBUTE) ?? null;
  }

  private clearTimers(timers: number[]): void {
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.length = 0;
  }
}

export { AdnamiSkinAdapter };
